import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../api-config';

/**
 * Sección de un tab de tipos de cambio.
 */
export interface ExRateTabSection {
  id: string;
  prefix: string;
  name: string;
  position: number;
  status: string;
}

/**
 * Tab de tipos de cambio con sus secciones.
 */
export interface ExRateTab {
  id: string;
  prefix: string;
  name: string;
  position: number;
  status: string;
  sections: ExRateTabSection[];
}

/**
 * Respuesta de la API para tabs de tipos de cambio.
 */
interface ExRateTabApiResponse {
  success: boolean;
  data: ExRateTab[];
}

/**
 * Service para gestionar los tabs de tipos de cambio.
 *
 * Endpoint:
 *   GET /api/settings/ex-rate-tabs/with-sections
 */
@Injectable({ providedIn: 'root' })
export class ExRateTabService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/ex-rate-tabs/with-sections`;
  }

  /**
   * Obtiene los tabs con sus secciones desde la API.
   */
  getTabsWithSections(): Observable<ExRateTab[]> {
    return this.httpClient
      .get(this.apiUrl, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => {
          const bodyText = resp.body as string | null;
          try {
            const response = bodyText
              ? (JSON.parse(bodyText) as ExRateTabApiResponse)
              : null;
            if (response && response.success && Array.isArray(response.data)) {
              return response.data
                .sort((a, b) => a.position - b.position)
                .map((tab) => ({
                  ...tab,
                  sections: (tab.sections || []).sort(
                    (a, b) => a.position - b.position,
                  ),
                }));
            }
            return [];
          } catch {
            return [];
          }
        }),
        catchError(() => of([])),
      );
  }
}
